import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, ScrollText, Globe, Shield, Link,
    Calendar, Loader2, Search, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, CheckCircle2, XCircle,
    X, Fingerprint, Hash, Clock, ChevronUp, ChevronDown
} from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError } from "../../utils/toast";
import { useClientLevel } from "../../hooks/useClientLevel";
import { ItemsPerPageDropdown } from "../../components/ItemsPerPageDropdown ";

interface ADGPO {
    id: number;
    created: string;
    updated: string;
    agent_id: number;
    client_id: number;
    asset_id: number;
    ad_domain_guid: string;
    guid: string;
    path: string;
    distinguished_name: string;
    display_name: string;
    name: string;
    domain: string;
    gpo_status: string;
    gpo_created_time: string;
    gpo_modified_time: string;
    when_changed: string;
    created_when: string;
    object_guid: string;
    modified_when: string;
    linked_to: string;
    gpc_functionality_version: string;
    is_critical_system_object: boolean;
    canonical_name: string;
}

const Val = ({ v }: { v: string | number | undefined | null }) => {
    const empty = v === undefined || v === null || v === "-" || v === "";
    return (
        <span className={empty ? " text-gray-200" : "text-gray-200"}>
            {empty ? "—" : String(v)}
        </span>
    );
};

// GPO status → color
const statusColor: Record<string, string> = {
    AllSettingsEnabled: "bg-green-500/10 border-green-500/20 text-green-400",
    AllSettingsDisabled: "bg-red-500/10 border-red-500/20 text-red-400",
    UserSettingsDisabled: "bg-yellow-500/10 border-yellow-500/20 text-yellow-400",
    ComputerSettingsDisabled: "bg-orange-500/10 border-orange-500/20 text-orange-400",
};

const statusLabel: Record<string, string> = {
    AllSettingsEnabled: "All Enabled",
    AllSettingsDisabled: "All Disabled",
    UserSettingsDisabled: "User Disabled",
    ComputerSettingsDisabled: "Computer Disabled",
};

// ─── Detail Panel ─────────────────────────────────────────────────────────────
function GPODetailPanel({ gpo, onClose }: { gpo: ADGPO; onClose: () => void }) {
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const Section = ({ title, icon, colorClass = "text-violet-400" }: { title: string; icon: React.ReactNode; colorClass?: string }) => (
        <div className="flex items-center gap-2 mb-3 mt-5 first:mt-0">
            <span className={colorClass}>{icon}</span>
            <span className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">{title}</span>
            <div className="flex-1 h-px bg-gray-700/50" />
        </div>
    );

    const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
        <div className="flex flex-col gap-0.5">
            <span className="text-[9px] text-gray-500 uppercase tracking-widest">{label}</span>
            <div className="text-[11px]">{children}</div>
        </div>
    );

    const statusCls = statusColor[gpo.gpo_status] ?? "bg-gray-500/10 border-gray-500/20 text-gray-200";

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-[1px]" onClick={onClose} />

            {/* Panel */}
            <div className="fixed top-0 right-0 h-full w-[30%] min-w-[340px] bg-gray-900 border-l border-gray-700 z-55 flex flex-col shadow-2xl animate-slide-in-right">

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/60 bg-gray-800/60 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-violet-600/20 border border-violet-500/30 flex items-center justify-center">
                            <ScrollText size={16} className="text-violet-400" />
                        </div>
                        <div>
                            <p className="text-[13px] font-bold text-white">{gpo.display_name}</p>
                            <p className="text-[10px] font-mono text-gray-200">{gpo.domain}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-gray-200 hover:text-white hover:bg-gray-700 transition-all">
                        <X size={15} />
                    </button>
                </div>

                {/* Pills row */}
                <div className="flex flex-wrap gap-1.5 px-5 py-3 border-b border-gray-700/40 bg-gray-800/30 shrink-0">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold border ${statusCls}`}>
                        <CheckCircle2 size={9} />
                        {statusLabel[gpo.gpo_status] ?? gpo.gpo_status}
                    </span>
                    {gpo.is_critical_system_object && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-red-500/10 border border-red-500/20 text-red-400">
                            <Shield size={9} /> Critical
                        </span>
                    )}
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold border ${gpo.linked_to
                        ? "bg-cyan-500/10 border-cyan-500/20 text-cyan-400"
                        : "bg-gray-500/10 border-gray-500/20 text-gray-500"
                        }`}>
                        <Link size={9} /> {gpo.linked_to ? "Linked" : "Not Linked"}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-gray-500/10 border border-gray-500/20 text-gray-200">
                        <Hash size={9} /> v{gpo.gpc_functionality_version}
                    </span>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1 text-gray-200">

                    {/* Identity */}
                    <Section title="Identity" icon={<ScrollText size={12} />} colorClass="text-violet-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Display Name"><Val v={gpo.display_name} /></Field>
                        <Field label="Name"><Val v={gpo.name} /></Field>
                        <Field label="Domain"><Val v={gpo.domain} /></Field>
                        <Field label="GPO Status">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[9px] font-bold ${statusCls}`}>
                                {statusLabel[gpo.gpo_status] ?? gpo.gpo_status}
                            </span>
                        </Field>
                        <Field label="Functionality Version">
                            <span className="font-mono text-gray-300">v{gpo.gpc_functionality_version}</span>
                        </Field>
                        <Field label="Critical">
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${gpo.is_critical_system_object ? "text-red-400" : " text-gray-200"}`}>
                                {gpo.is_critical_system_object ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
                                {gpo.is_critical_system_object ? "Yes" : "No"}
                            </span>
                        </Field>
                    </div>

                    {/* Linked To */}
                    <Section title="Linked To" icon={<Link size={12} />} colorClass="text-cyan-400" />
                    {gpo.linked_to ? (
                        <p className="font-mono text-[10px] text-cyan-300/80 break-all bg-cyan-500/5 border border-cyan-500/10 rounded-lg px-3 py-2">
                            {gpo.linked_to}
                        </p>
                    ) : (
                        <p className="text-[10px]  text-gray-200">Not linked to any OU</p>
                    )}

                    {/* Timestamps */}
                    <Section title="Timestamps" icon={<Clock size={12} />} colorClass="text-purple-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="GPO Created">
                            <span className="font-mono text-[10px] text-gray-300"><Val v={gpo.gpo_created_time} /></span>
                        </Field>
                        <Field label="GPO Modified">
                            <span className="font-mono text-[10px] text-gray-300"><Val v={gpo.gpo_modified_time} /></span>
                        </Field>
                        <Field label="When Changed">
                            <span className="font-mono text-[10px] text-gray-300"><Val v={gpo.when_changed} /></span>
                        </Field>
                        <Field label="Modified When">
                            <span className="font-mono text-[10px] text-gray-300"><Val v={gpo.modified_when} /></span>
                        </Field>
                        <Field label="Created When">
                            <span className="font-mono text-[10px] text-gray-300"><Val v={gpo.created_when} /></span>
                        </Field>
                    </div>

                    {/* Identifiers */}
                    <Section title="Identifiers" icon={<Fingerprint size={12} />} colorClass="text-rose-400" />
                    <div className="flex flex-col gap-3">
                        <Field label="GUID">
                            <span className="font-mono text-[10px] text-violet-300/80 break-all"><Val v={gpo.guid} /></span>
                        </Field>
                        <Field label="Object GUID">
                            <span className="font-mono text-[10px] text-gray-200 break-all"><Val v={gpo.object_guid} /></span>
                        </Field>
                        <Field label="Canonical Name">
                            <span className="font-mono text-[10px] text-gray-200 break-all"><Val v={gpo.canonical_name} /></span>
                        </Field>
                        <Field label="Distinguished Name">
                            <span className="font-mono text-[10px] text-gray-200 break-all"><Val v={gpo.distinguished_name} /></span>
                        </Field>
                        <Field label="Path">
                            <span className="font-mono text-[10px] text-gray-500 break-all"><Val v={gpo.path} /></span>
                        </Field>
                    </div>

                    <div className="h-6" />
                </div>
            </div>
        </>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ADGPOs({ asset_id }: { asset_id: any }) {
    const [gpos, setGPOs] = useState<ADGPO[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selectedGPO, setSelectedGPO] = useState<ADGPO | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    const level = useClientLevel();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchGPOs = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;

            if (debouncedSearch) {
                queryObj.$or = [
                    { name: { $ilike: `%${debouncedSearch}%` } },
                    { display_name: { $ilike: `%${debouncedSearch}%` } },
                    { domain: { $ilike: `%${debouncedSearch}%` } },
                    { gpo_status: { $ilike: `%${debouncedSearch}%` } },
                    { linked_to: { $ilike: `%${debouncedSearch}%` } },
                    { distinguished_name: { $ilike: `%${debouncedSearch}%` } },
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit
            };

            // Add API Sorting if requested
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/ad_gpos", { params });

            if (response.data.status) {
                setGPOs(response.data.data);
                setTotalRecords(response.data.total ?? response.data.data.length);
            } else {
                showError("Failed to load GPOs");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    }, [level, debouncedSearch, currentPage, limit, sortConfig, asset_id]);

    useEffect(() => {
        fetchGPOs();
    }, [fetchGPOs]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setCurrentPage(1); // Reset to page 1 on sort
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, icon }: { label: string; sortKey: string; icon?: React.ReactNode }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group select-none"
            >
                <div className="flex items-center gap-2">
                    {icon}
                    {label}
                    <div className="flex items-center ml-auto">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-violet-400" />
                            ) : (
                                <ChevronDown size={12} className="text-violet-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    return (
        <div className="flex flex-col bg-transparent text-gray-200">

            {selectedGPO && (
                <GPODetailPanel gpo={selectedGPO} onClose={() => setSelectedGPO(null)} />
            )}

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-violet-600/20 p-1.5 rounded-lg border border-violet-500/30">
                        <ScrollText size={16} className="text-violet-400" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">Group Policy Objects</h1>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200 group-focus-within:text-violet-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search GPOs..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-violet-500/50 transition-all"
                        />
                    </div>
                    <button
                        onClick={fetchGPOs}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0 disabled:opacity-50"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-violet-400"} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <SortableHeader label="Policy Name" sortKey="display_name" icon={<ScrollText size={13} className="text-violet-400" />} />
                                <SortableHeader label="Domain" sortKey="domain" icon={<Globe size={13} className="text-indigo-400" />} />
                                <SortableHeader label="Status" sortKey="gpo_status" icon={<CheckCircle2 size={13} className="text-green-400" />} />
                                <SortableHeader label="Linked To" sortKey="linked_to" icon={<Link size={13} className="text-cyan-400" />} />
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                    <div className="flex items-center gap-2"><Hash size={13} className="text-gray-400" /> Version</div>
                                </th>
                                <SortableHeader label="Critical" sortKey="is_critical_system_object" icon={<Shield size={13} className="text-red-400" />} />
                                <SortableHeader label="Created" sortKey="gpo_created_time" icon={<Calendar size={13} className="text-orange-400" />} />
                                <SortableHeader label="Modified" sortKey="gpo_modified_time" icon={<Clock size={13} className="text-pink-400" />} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-500">
                                            <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading GPOs...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : gpos.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <ScrollText size={24} className="text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">
                                                {debouncedSearch ? `No results for "${debouncedSearch}"` : "No GPOs found"}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                gpos.map((gpo) => (
                                    <tr
                                        key={gpo.id}
                                        onClick={() => setSelectedGPO(gpo)}
                                        className={`hover:bg-gray-800/60 transition-colors cursor-pointer ${selectedGPO?.id === gpo.id
                                            ? "bg-violet-500/5 border-l-2 border-l-violet-500"
                                            : ""
                                            }`}
                                    >
                                        {/* Policy Name */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full bg-violet-600/20 border border-violet-500/30 flex items-center justify-center shrink-0">
                                                    <ScrollText size={11} className="text-violet-400" />
                                                </div>
                                                <div>
                                                    <p className="text-[11px] font-bold text-gray-100">{gpo.display_name}</p>
                                                    <p className="text-[9px] font-mono text-gray-500 truncate max-w-[180px]" title={gpo.guid}>
                                                        {gpo.guid}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Domain */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[11px] text-gray-300"><Val v={gpo.domain} /></span>
                                        </td>

                                        {/* Status */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[9px] font-bold ${statusColor[gpo.gpo_status] ?? "bg-gray-500/10 border-gray-500/20 text-gray-200"}`}>
                                                <CheckCircle2 size={9} />
                                                {statusLabel[gpo.gpo_status] ?? gpo.gpo_status}
                                            </span>
                                        </td>

                                        {/* Linked To */}
                                        <td className="px-4 py-3 max-w-[200px]">
                                            {gpo.linked_to ? (
                                                <span
                                                    className="text-[10px] font-mono text-cyan-300/80 truncate block"
                                                    title={gpo.linked_to}
                                                >
                                                    {gpo.linked_to}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[10px]  text-gray-200">
                                                    <XCircle size={10} /> Not linked
                                                </span>
                                            )}
                                        </td>

                                        {/* Version */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-700/50 border border-gray-600/30 rounded text-[9px] font-mono font-bold text-gray-300">
                                                v{gpo.gpc_functionality_version}
                                            </span>
                                        </td>

                                        {/* Critical */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {gpo.is_critical_system_object ? (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-400">
                                                    <CheckCircle2 size={11} /> Yes
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold  text-gray-200">
                                                    <XCircle size={11} /> No
                                                </span>
                                            )}
                                        </td>

                                        {/* Created */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[10px] font-mono text-gray-200">
                                                {gpo.gpo_created_time || "—"}
                                            </span>
                                        </td>

                                        {/* Modified */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[10px] font-mono text-gray-200">
                                                {gpo.gpo_modified_time || "—"}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1  text-gray-200">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="text-[9px] text-violet-400 hover:text-violet-300 font-bold uppercase tracking-widest transition-colors"
                            >
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-violet-500/10 border border-violet-500/20 text-violet-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}